/**
 * PaginationBar — honesty-cluster footer for the WorkDesk queue + my-cases
 * tabs.
 *
 * Phase 0 (carry-forward plan §1.2 step 4) extracted the wiring-only version
 * from TabShell.tsx:168-196. Phase 2 (D1, docs/plans/2026-08-22-accountant-
 * workdesk-design-spec-K3.md "Footer / PaginationBar") restyles it to the
 * honesty-cluster form: a left DM Mono cluster that discloses page-scope
 * ("1–50 OF 132", or "12 OF 50 ON THIS PAGE · 132 TOTAL" when a client-side
 * filter/search is active — "the single place that discloses page-scope —
 * never imply a filtered count is global"), and a right PAGE n / N indicator
 * with Prev/Next controls.
 *
 * The bar renders whenever `totalCount > 0`; the Prev/Next *controls* render
 * only when `totalPages > 1` — the honesty cluster is valuable on a single
 * page too (per the D1 spec, this guard is on the controls, not the bar).
 */
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationBarProps {
  page: number;
  totalPages: number;
  /** 1-based start of the current page's range within totalCount, e.g. 1. */
  rangeStart: number;
  /** 1-based end of the current page's range within totalCount, e.g. 50. */
  rangeEnd: number;
  /** Server total across all pages. */
  totalCount: number;
  /**
   * Rows actually shown after a client-side search/status filter narrowed
   * the page. Pass `null` when no filter/search is active — the bar then
   * renders the plain "rangeStart–rangeEnd OF totalCount" form. A non-null
   * value (including 0) switches to the "shown OF pageRowCount ON THIS PAGE
   * · totalCount TOTAL" disclosure form.
   */
  shownCount: number | null;
  /** Rows on this page BEFORE the client-side filter/search (i.e. the raw fetched page size). */
  pageRowCount: number;
  onPrev: () => void;
  onNext: () => void;
}

export default function PaginationBar({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  totalCount,
  shownCount,
  pageRowCount,
  onPrev,
  onNext,
}: PaginationBarProps) {
  if (totalCount <= 0) return null;

  const isFiltered = shownCount !== null;

  return (
    <nav
      className="px-4 py-2.5 border-t border-border flex items-center justify-between"
      aria-label="Case list pagination"
    >
      <span className="font-mono text-xs text-text-muted" aria-live="polite">
        {isFiltered
          ? `${shownCount} OF ${pageRowCount} ON THIS PAGE · ${totalCount} TOTAL`
          : `${rangeStart}–${rangeEnd} OF ${totalCount}`}
      </span>

      {totalPages > 1 && (
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-text-muted">
            PAGE {page} / {totalPages}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onPrev}
              disabled={page <= 1}
              className="h-7 px-2.5 inline-flex items-center gap-1 border border-border rounded-[2px] text-[11px] font-medium uppercase tracking-wide text-text-muted hover:text-brand hover:border-brand/40 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-muted disabled:hover:border-border"
              aria-label="Go to previous page"
            >
              <ChevronLeft className="w-3 h-3" aria-hidden="true" />
              PREV
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={page >= totalPages}
              className="h-7 px-2.5 inline-flex items-center gap-1 border border-border rounded-[2px] text-[11px] font-medium uppercase tracking-wide text-text-muted hover:text-brand hover:border-brand/40 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-muted disabled:hover:border-border"
              aria-label="Go to next page"
            >
              NEXT
              <ChevronRight className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
